import { Box } from '@chakra-ui/react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme } from '../shared/theme';

interface Props {
  x: number;
  y: number;
  content: React.ReactNode;
  onMouseLeave?: () => void;
  anchorBottomLeft?: boolean; // if true, (x,y) is baseline of anchor, tooltip placed above and right of that x
}

export function SvgTooltip({ x, y, content, onMouseLeave, anchorBottomLeft = false }: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [layerEl, setLayerEl] = useState<SVGGElement | null>(null);

  // Measure content dimensions once it is rendered
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    // scrollWidth/scrollHeight give the full size of the element's content without clipping,
    // even if current CSS sets a smaller width that causes wrapping. This prevents the
    // iterative "grow a few pixels each time" effect we observed.
    const paddedWidth = Math.round(el.scrollWidth) + 16; // 8px padding both sides
    const paddedHeight = Math.round(el.scrollHeight) + 16; // 8px padding top/bottom

    setSize(prev =>
      prev.width === paddedWidth && prev.height === paddedHeight ? prev : { width: paddedWidth, height: paddedHeight },
    );
    // Depend on scrollWidth changes indirectly via content changes.
  }, [content]);

  // Observe size changes (e.g., after expanding "..." list) and update tooltip dimensions immediately.
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width < 10 || height < 10) return; // ignore spurious zero-size measurements

      const paddedWidth = Math.round(width) + 16;
      const paddedHeight = Math.round(height) + 16;
      setSize(prev =>
        prev.width === paddedWidth && prev.height === paddedHeight
          ? prev
          : { width: paddedWidth, height: paddedHeight },
      );
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    const svg = contentRef.current.closest('svg');
    if (!svg) return;

    let layer = svg.querySelector('#svg-tooltip-layer');
    if (!layer) {
      layer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      layer.setAttribute('id', 'svg-tooltip-layer');
      svg.appendChild(layer); // append as last child => top of stacking order
    }
    setLayerEl(layer as SVGGElement);
  }, []);

  // Fallback to sensible defaults while measuring.
  const tooltipWidth = size.width || 300;
  const tooltipHeight = size.height || 100;

  const tooltipX = anchorBottomLeft ? x + 4 : x;
  const tooltipY = anchorBottomLeft ? y - tooltipHeight : y - tooltipHeight + 4;

  // Freeze position after first calculation to avoid jitter when size updates
  const frozenPosRef = useRef<{ x: number; y: number } | null>(null);
  if (!frozenPosRef.current) {
    frozenPosRef.current = { x: tooltipX, y: tooltipY };
  }
  const finalX = frozenPosRef.current.x;
  const finalY = frozenPosRef.current.y;

  const handleMouseLeave = (e: React.MouseEvent<SVGElement>) => {
    onMouseLeave?.();
    e.stopPropagation();
  };
  const handleMouseEnter = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
  };

  const tooltipGroup = (
    <g onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
      <rect x={finalX} y={finalY} width={tooltipWidth} height={tooltipHeight} fill="transparent" />
      <foreignObject x={finalX} y={finalY} width={tooltipWidth} height={tooltipHeight}>
        <Box
          ref={contentRef}
          bg={theme.colors.neutral50}
          p={2}
          borderRadius="md"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.200"
          w="max-content"
          tabIndex={-1}
          style={{ outline: 'none' }}
        >
          {content}
        </Box>
      </foreignObject>
    </g>
  );

  // If we have a tooltip layer, render there; otherwise inline (fallback)
  return layerEl ? createPortal(tooltipGroup, layerEl) : tooltipGroup;
}
